/**
 * GroupService.ts
 * 
 * 职责：
 * 1. 管理元素的组合状态和组合操作
 * 2. 提供创建组合和解除组合的功能
 * 3. 检查元素是否属于某个组合
 * 4. 获取组合内的所有元素
 * 5. 处理组合的选择和操作
 */

import { GroupData, GroupElement } from '../../types';

export class GroupService {
  private groups: GroupData[] = [];

  constructor(initialGroups: GroupData[] = []) {
    this.groups = [...initialGroups];
  }

  // 获取所有组合
  public getGroups(): GroupData[] {
    return [...this.groups];
  }

  // 设置组合数据
  public setGroups(groups: GroupData[]): void {
    this.groups = [...groups];
  }

  // 创建新组合
  public createGroup(elements: GroupElement[]): string {
    if (elements.length < 2) {
      return ''; // 至少需要两个元素才能组合
    }

    // 生成唯一ID
    const groupId = `group-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // 创建新组合
    const newGroup: GroupData = {
      id: groupId,
      elementIds: [...elements]
    };
    
    // 添加到组合列表
    this.groups.push(newGroup);
    
    // 触发工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    
    // 立即触发渲染，确保组合框立即显示
    this.triggerRender();
    
    return groupId;
  }
  
  // 解除组合
  public ungroup(groupId: string): boolean {
    const index = this.groups.findIndex(group => group.id === groupId);
    if (index === -1) {
      return false;
    }
    
    // 移除组合
    this.groups.splice(index, 1);
    
    // 触发工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    
    // 立即触发渲染，确保组合解除后立即更新视图
    this.triggerRender();
    
    return true;
  }
  
  // 向现有组合添加元素
  public addElementsToGroup(groupId: string, elements: GroupElement[]): boolean {
    const groupIndex = this.groups.findIndex(group => group.id === groupId);
    if (groupIndex === -1 || elements.length === 0) {
      return false;
    }
    
    // 获取组合
    const group = this.groups[groupIndex];
    
    // 添加新元素，避免重复
    elements.forEach(element => {
      const existsInGroup = group.elementIds.some(
        existingElement => existingElement.type === element.type && existingElement.index === element.index
      );
      
      if (!existsInGroup) {
        group.elementIds.push(element);
      }
    });
    
    // 更新组合
    this.groups[groupIndex] = { ...group };
    
    // 触发工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    
    // 立即触发渲染，确保添加元素后立即更新视图
    this.triggerRender();
    
    return true;
  }
  
  // 触发渲染
  private triggerRender(): void {
    // 多种方式触发渲染，确保在各种情况下都能正常工作
    try {
      // 1. 尝试通过DOM事件触发Canvas渲染
      const canvasElement = document.querySelector('canvas');
      if (canvasElement) {
        canvasElement.dispatchEvent(new CustomEvent('render-required'));
      }
      
      // 2. 通过document级别事件触发渲染
      document.dispatchEvent(new CustomEvent('render-required'));
      
      // 3. 使用微任务确保在当前执行上下文之后立即执行
      Promise.resolve().then(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
      
      // 4. 使用requestAnimationFrame确保在下一帧渲染
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('render-required'));
      });
    } catch (error) {
      // 渲染触发失败，但不中断执行
    }
  }
  
  // 检查元素是否在任何组合中
  public isElementInAnyGroup(type: 'line' | 'text' | 'card' | 'image', index: number): string {
    for (const group of this.groups) {
      if (this.isElementInGroup(group.id, type, index)) {
        return group.id;
      }
    }
    return '';
  }
  
  // 检查元素是否在指定组合中
  public isElementInGroup(groupId: string, type: 'line' | 'text' | 'card' | 'image', index: number): boolean {
    const group = this.groups.find(g => g.id === groupId);
    if (!group) {
      return false;
    }
    
    return group.elementIds.some(element => element.type === type && element.index === index);
  }
  
  // 获取组合中的所有元素
  public getGroupElements(groupId: string): GroupElement[] {
    const group = this.groups.find(g => g.id === groupId);
    return group ? [...group.elementIds] : [];
  }
  
  // 获取包含指定元素的组合
  public getGroupByElement(type: 'line' | 'text' | 'card' | 'image', index: number): GroupData | null {
    const groupId = this.isElementInAnyGroup(type, index);
    if (!groupId) {
      return null;
    }
    
    return this.groups.find(g => g.id === groupId) || null;
  }
  
  // 删除特定元素后更新组合
  public removeElementFromGroups(type: 'line' | 'text' | 'card' | 'image', index: number): void {
    // 标记是否有任何组合发生了变化
    let groupsChanged = false;
    
    // 遍历所有组合检查和调整索引
    for (let i = 0; i < this.groups.length; i++) {
      // 从组合中移除被删除的元素
      const group = this.groups[i];
      const initialLength = group.elementIds.length;
      
      group.elementIds = group.elementIds.filter(element => 
        !(element.type === type && element.index === index)
      );
      
      // 调整索引大于被删除元素索引的元素
      group.elementIds.forEach(element => {
        if (element.type === type && element.index > index) {
          element.index--;
        }
      });
      
      // 如果组合中元素少于2个，删除该组合
      if (group.elementIds.length < 2) {
        this.groups.splice(i, 1);
        i--; // 调整遍历索引
        groupsChanged = true;
      } else if (group.elementIds.length !== initialLength) {
        // 只有在组合发生了变化的情况下才更新
        this.groups[i] = { ...group };
        groupsChanged = true;
      }
    }
    
    // 如果有任何组合发生了变化，触发工具操作完成事件
    if (groupsChanged) {
      document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    }
  }

  // 移动元素后更新组合状态
  public updateGroupsAfterMove(): void {
    // 这个方法是一个占位符，可能在以后的实现中用于移动元素后更新组合状态
  }

  // 重置所有组合
  public resetGroups(): void {
    this.groups = [];
    
    // 触发工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
  }
} 